import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'

export const DROP_ALLOWED_CLASS = 'schedule-drop-allowed'
export const DROP_BLOCKED_CLASS = 'schedule-drop-blocked'

type DropColumn = { resourceId: string; staffId?: number }
type DraggedRow = Partial<Pick<IPatientSchedule, 'emr_group_id' | 'consultation_staff'>>

export type JointDropColumns = { allowed: string[]; blocked: string[] }

const NO_COLUMNS: JointDropColumns = { allowed: [], blocked: [] }

// A joint visit's row keeps its doctor, so of the doctors' waiting-room columns only its own takes it.
export const jointDropColumns = (
  row: DraggedRow | undefined,
  columns: readonly DropColumn[],
): JointDropColumns => {
  if (row?.emr_group_id == null) return NO_COLUMNS
  const ownId = Number(row.consultation_staff)
  const doctorColumns = columns.filter((column) => column.staffId != null)
  return {
    allowed: doctorColumns
      .filter((column) => column.staffId === ownId)
      .map((column) => column.resourceId),
    blocked: doctorColumns
      .filter((column) => column.staffId !== ownId)
      .map((column) => column.resourceId),
  }
}

export const isJointDropBlocked = (
  row: DraggedRow | undefined,
  columns: readonly DropColumn[],
  resourceId: string,
): boolean => jointDropColumns(row, columns).blocked.includes(resourceId)

const markColumns = (
  root: ParentNode,
  columnKeys: readonly string[],
  resourceIds: string[],
  className: string,
): void =>
  resourceIds
    .map((resourceId) => columnKeys.indexOf(resourceId))
    .filter((index) => index >= 0)
    .forEach((index) =>
      root
        .querySelectorAll(`.e-work-cells[data-group-index="${index}"]`)
        .forEach((cell) => cell.classList.add(className)),
    )

// Shows where a dragged joint visit can land; cells keep their place, so marks follow the column index.
export const paintJointDropColumns = (
  root: ParentNode | null | undefined,
  columnKeys: readonly string[],
  columns: JointDropColumns,
): void => {
  if (!root) return
  markColumns(root, columnKeys, columns.allowed, DROP_ALLOWED_CLASS)
  markColumns(root, columnKeys, columns.blocked, DROP_BLOCKED_CLASS)
}

export const clearJointDropColumns = (root: ParentNode | null | undefined): void =>
  root
    ?.querySelectorAll(`.${DROP_ALLOWED_CLASS}, .${DROP_BLOCKED_CLASS}`)
    .forEach((cell) => cell.classList.remove(DROP_ALLOWED_CLASS, DROP_BLOCKED_CLASS))
